import { useMemo, useState } from "react";
import { openBlockingPatientIssuesPdf } from "../blockingIssuesReport";
import { launchStatusLabel } from "../controleVariants";
import {
    countPatients,
    findBlockingPatientIssues,
    launchStatusPatch,
    sortPatientsForControl,
    withAllPointsValidated,
    withPatientPoint,
    withPatientSharedComment,
    withPatientValidation,
} from "../patientControlForm";

const NEVER_FROZEN = () => false;

// Saisie du dossier ouvert : champs de la fiche et contrôles patients. Un patient figé (isFrozen) n'est plus modifié.
export function usePatientControlActions(workspace, { user, isFrozen = NEVER_FROZEN }) {
    const { setFormData } = workspace;
    const updatePatient = (patientId, update) => {
        setFormData(data => (isFrozen(patientId, data) ? data : update(data)));
    };
    const updatePatientControl = (patientId, pointKey, patch) => {
        updatePatient(patientId, data => withPatientPoint(data, patientId, pointKey, patch));
    };
    return {
        updateMeta: (key, value) => setFormData(data => ({ ...data, meta: { ...data.meta, [key]: value } })),
        updateCommentaire: (key, value) => setFormData(data => ({ ...data, commentaires: { ...(data.commentaires || {}), [key]: value } })),
        updatePatientControl,
        updatePatientValidation: (patientId, validated) => updatePatient(patientId, data => withPatientValidation(data, patientId, validated)),
        validateAllPatientPoints: patientId => updatePatient(patientId, data => withAllPointsValidated(data, patientId)),
        updatePatientSharedControlComment: (patientId, comment) => updatePatient(patientId, data => withPatientSharedComment(data, patientId, comment)),
        updatePatientLaunchStatus: (patientId, launchStatus) => updatePatientControl(patientId, "tracageLogiciels", launchStatusPatch(launchStatus, user)),
    };
}

// Synthèse des contrôles patients : patients à reprendre, compteurs et ordre d'affichage.
export function usePatientControlSummary({ patients, controls, variant, controleMode, useSharedComment = false }) {
    const blockingPatientIssues = useMemo(
        () => findBlockingPatientIssues({ patients, controls, variant, controleMode, useSharedComment }),
        [controleMode, controls, patients, useSharedComment, variant]
    );
    const counts = useMemo(() => countPatients(patients, controls), [controls, patients]);
    const sortedControlePatients = useMemo(() => sortPatientsForControl(patients, controls), [controls, patients]);
    return {
        blockingPatientIssues,
        activeControlePatientCount: counts.active,
        validatedPatientCount: counts.validated,
        remainingPatientCount: counts.remaining,
        sortedControlePatients,
    };
}

// Impression du rapport des patients à reprendre, avec confirmation tant que des patients restent à valider.
export function usePrintReport({ variant, workspace, issues, remainingPatientCount }) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const openReport = () => {
        openBlockingPatientIssuesPdf({
            dossier: workspace.activeDossier,
            launchStatusLabel: value => launchStatusLabel(variant, value),
            ehpadName: workspace.activeDossier?.ehpadNom || workspace.selectedEhpad?.nom,
            issues,
        });
    };
    return {
        confirmOpen,
        print: () => (remainingPatientCount > 0 ? setConfirmOpen(true) : openReport()),
        cancel: () => setConfirmOpen(false),
        confirm: () => {
            setConfirmOpen(false);
            openReport();
        },
    };
}
